import React, { useEffect } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useLocation } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { usePolicyDocument } from '../../hooks/usePolicyDocument';
import PolicyNavigation from './PolicyNavigation';
import { PolicyMarkdown, processPolicyContent } from './policyMarkdown';
import { policyVersionLine } from './policySanitize';

const Container = styled.div`
  ${tw`min-h-screen bg-bg-secondary py-8 pb-8 md:pb-16 px-4`}
`;

const PageWrapper = styled.div`
  ${tw`w-full max-w-full min-w-0 max-w-7xl mx-auto box-border`}

  @media (min-width: 768px) {
    ${tw`flex gap-6 items-start`}
  }
`;

const ContentWrapper = styled.div`
  ${tw`bg-white rounded-lg shadow-card-hover p-8 flex-1`}

  @media (min-width: 768px) {
    ${tw`max-w-none`}
  }
`;

const NavigationWrapper = styled.div`
  ${tw`w-full mb-6`}

  @media (min-width: 768px) {
    ${tw`w-64 flex-shrink-0 mb-0`}
  }
`;

const Title = styled.h1`
  ${tw`text-4xl font-bold mb-6 text-text-primary`}
`;

const VersionLine = styled.p`
  ${tw`text-sm text-text-secondary mb-6`}
`;

const LoadingContainer = styled.div`
  ${tw`flex justify-center items-center min-h-screen`}
`;

interface PolicyPageProps {
  title: string;
  contentPath: string | null;
  /** GraphQL PolicyKind. The static file at contentPath is the fallback. */
  kind: string;
  /** An older version (`/policies/:doc/versions/:id`). */
  versionId?: string | null;
}

const PolicyPage: React.FC<PolicyPageProps> = ({ title, contentPath, kind, versionId }) => {
  const location = useLocation();
  const policy = usePolicyDocument({ kind, contentPath, versionId });
  const content = policy.text ? processPolicyContent(policy.text) : '';
  const loading = !policy.ready;
  const error = policy.error;
  const versionLine = policyVersionLine(policy);

  // Handle anchor link clicks for smooth scrolling
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        const href = target.getAttribute('href');
        if (href) {
          const id = href.substring(1);
          const element = document.getElementById(id);
          if (element) {
            e.preventDefault();
            const offsetTop = element.offsetTop - 80; // Account for fixed header
            window.scrollTo({
              top: offsetTop,
              behavior: 'smooth',
            });
            // Update URL without triggering scroll
            window.history.pushState(null, '', href);
          }
        }
      }
    };

    // Wait for content to be rendered
    if (!loading && content) {
      document.addEventListener('click', handleAnchorClick);
      return () => {
        document.removeEventListener('click', handleAnchorClick);
      };
    }
  }, [loading, content]);

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <Container>
        <PageWrapper>
          <NavigationWrapper>
            <PolicyNavigation currentPath={location.pathname} />
          </NavigationWrapper>
          <ContentWrapper>
            <div className="text-red-600">{error}</div>
          </ContentWrapper>
        </PageWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <PageWrapper>
        <NavigationWrapper>
          <PolicyNavigation currentPath={location.pathname} />
        </NavigationWrapper>
        <ContentWrapper>
          <Title>{title}</Title>
          {versionLine && (
            <VersionLine data-testid="policy-version-line">{versionLine}</VersionLine>
          )}
          <PolicyMarkdown content={content} />
        </ContentWrapper>
      </PageWrapper>
    </Container>
  );
};

export default PolicyPage;
